import { useMemo, useEffect } from 'react';
import { orderBy, partition, trim, sortBy, deburr } from 'lodash';
import { Divider, Tooltip, IconButton, styled } from '@mui/material';
import Unsubscribe from '@mui/icons-material/Unsubscribe';
import FieldRow from 'components/FormElements/FieldRow';
import RecordView from 'components/RecordView';
import AppTable from 'components/Table';
import Spacing from 'components/Spacing';
import { DeliveryType } from 'further-types/custom-updates';
import { Api } from 'further-types/custom-updates';
import { usePagination } from 'further-ui/hooks';
import { sanitiseHtml, pluralize } from 'further-ui/utils';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  verticalAlign: 'middle',
}));

const StyledUnsubscribeIcon = styled(Unsubscribe)(({ theme }) => ({
  fontSize: theme.fontSizes.xxl,
}));

type Props = any; //TODO: define props

const Summary: React.FC<Props> = ({
  data,
  classes,
  selectedTranchesOrCompanies,
}) => {
  const pagination = usePagination<
    'name' | 'email' | 'otherUsersEmailsDisplay' | 'type'
  >(
    {
      id: 'custom-update-summary-table',
      order: 'asc',
      orderBy: 'name',
    },
    ['name', 'email', 'otherUsersEmailsDisplay', 'type'],
  );

  useEffect(() => {
    pagination.toFirstPage();
  }, [data.recipients?.length, data.recipientFilter]);

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sort: true,
      render: (row: Api.Recipient) => (
        <>
          {row.name}
          {data.deliveryToInvestors === DeliveryType.EmailAndPortal &&
            row.type === 'Investor' &&
            row.disableAllEmails && (
              <Tooltip title="This investor's emails are disabled so they will only receive the update in the portal and not via email">
                <StyledIconButton size="extra-small">
                  <StyledUnsubscribeIcon />
                </StyledIconButton>
              </Tooltip>
            )}
        </>
      ),
    },
    { key: 'email', label: 'Primary user email', sort: true },
    { key: 'otherUsersEmailsDisplay', label: 'Other users emails', sort: true },
    { key: 'type', label: 'User type', sort: true },
  ];

  let title = 'Recipients';
  if (data.recipientFilter === Api.RecipientFilter.Tranche) {
    title = 'Tranches';
  } else if (data.recipientFilter === Api.RecipientFilter.Company) {
    title = 'Companies';
  }

  let recipientDescription = '';
  if (
    data.recipientFilter === Api.RecipientFilter.Company ||
    data.recipientFilter === Api.RecipientFilter.Tranche
  ) {
    recipientDescription = selectedTranchesOrCompanies?.join(', ') || '-';
  } else if (data.recipientFilter === Api.RecipientFilter.Specific) {
    recipientDescription = 'Sent to specific investors';
  } else if (data.recipientFilter === Api.RecipientFilter.All) {
    recipientDescription = 'Sent to all registered users';
  } else if (data.recipientFilter === Api.RecipientFilter.Subscriptions) {
    recipientDescription =
      'Sent to all registered investors with subscriptions';
  } else if (data.recipientFilter === Api.RecipientFilter.NoSubscriptions) {
    recipientDescription =
      'Sent to all registered investors without subscriptions';
  }

  const sortedRows = useMemo(() => {
    if (!data.recipients?.length) {
      return [];
    }

    const preparedRows = data.recipients.map((r) => {
      const sortedOtherEmails = sortBy(r.otherUsersEmails ?? [], (e) =>
        e.toLowerCase(),
      );

      return {
        ...r,
        otherUsersEmailsDisplay: sortedOtherEmails.join(', '),
        _sortOtherUsersEmails: sortedOtherEmails[0] ?? '',
      };
    });

    if (pagination.orderBy === 'otherUsersEmailsDisplay') {
      const [rowsWithValues, rowsWithoutValues] = partition(
        preparedRows,
        (row) => trim(row._sortOtherUsersEmails) !== '',
      );

      const sorted = orderBy(
        rowsWithValues,
        (row) => row._sortOtherUsersEmails.toLowerCase(),
        [pagination.order ?? 'asc'],
      );

      return [...sorted, ...rowsWithoutValues];
    }

    // Ensure rows with empty/whitespace-only names appear after sorted rows
    if (pagination.orderBy === 'name') {
      const [rowsWithNames, rowsWithoutNames] = partition(
        preparedRows,
        (row) => {
          const value = typeof row.name === 'string' ? row.name : '';
          return trim(value) !== '';
        },
      );

      const sortedByName = orderBy(
        rowsWithNames,
        (row) =>
          // use deburr to ensure diacritics are handled correctly
          deburr(String(row.name ?? '')).toLowerCase(),
        [pagination.order ?? 'asc'],
      );

      return [...sortedByName, ...rowsWithoutNames];
    }

    return orderBy(
      preparedRows,
      (row) => {
        let value = row[pagination.orderBy];
        if (pagination.orderBy === 'name' && typeof value === 'string') {
          // use deburr to ensure diacritics are handled correctly
          value = deburr(value);
        }
        return value != null ? String(value).toLowerCase() : '';
      },
      [pagination.order ?? 'asc'],
    );
  }, [data.recipients, pagination.order, pagination.orderBy]);

  const paginatedRows = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.rowsPerPage;
    return sortedRows.slice(startIndex, startIndex + pagination.rowsPerPage);
  }, [sortedRows, pagination.page, pagination.rowsPerPage]);

  return (
    <>
      <RecordView>
        <FieldRow title={'Update type'}>Custom update</FieldRow>
        <FieldRow title={title}>{recipientDescription}</FieldRow>
        <FieldRow title="Delivery">
          {data.deliveryToInvestors === DeliveryType.EmailAndPortal
            ? 'Email and portal'
            : data.deliveryToInvestors === DeliveryType.Email
            ? 'Email'
            : 'Portal'}
        </FieldRow>
      </RecordView>
      <Divider className={classes.divider} />
      <RecordView>
        <FieldRow title="Subject line">{data?.subject || '-'}</FieldRow>
        <FieldRow title="Body title">{data?.title || '-'}</FieldRow>
        <FieldRow title="Body text" introLineTopTitle>
          <>
            <div>Dear $recipientname,</div>
            <div
              className="text-editor"
              dangerouslySetInnerHTML={{
                __html: sanitiseHtml(data?.emailBody) ?? '',
              }}
            />
          </>
        </FieldRow>

        {data.attachments?.length > 0 && (
          <FieldRow title={pluralize('Attachment', data?.attachments?.length)}>
            {data?.attachments?.map((attachment, index) => {
              return (
                <div key={index} className="mb-1">
                  <a
                    key={index}
                    href={attachment?.preview || attachment?.url}
                    rel="noreferrer"
                    target={'_blank'}
                    className={classes.attachmentLink}
                  >
                    {attachment?.title || 'Attachment'}
                  </a>
                </div>
              );
            })}
          </FieldRow>
        )}

        <FieldRow title="Include UK financial promotions notice at top of email">
          {data.includeFinancialPromotionsNotice ? 'Yes' : 'No'}
        </FieldRow>
        {data.includeFinancialPromotionsNotice && (
          <FieldRow title="Learn more URL">
            {data.financialPromotionsLearnMoreUrl}
          </FieldRow>
        )}
      </RecordView>
      {data.recipients?.length > 0 && (
        <>
          <Divider className={classes.divider} />
          <Spacing size="md">
            <h3>Recipient list</h3>
            <AppTable
              columns={columns}
              tablebody={paginatedRows}
              pagination={true}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              count={data.recipients?.length || 0}
              onPageChange={pagination.handleChangePage}
              onRowsPerPageChange={pagination.handleChangeRowsPerPage}
              onRequestSort={(_, orderBy) => {
                pagination.handleRequestSort(_, orderBy);
              }}
              order={pagination.order}
              orderBy={pagination.orderBy}
            />
          </Spacing>
        </>
      )}
    </>
  );
};

export default Summary;
